/**
 * @module app.shared
 */

/**
 * Escapes any special regex characters in a string `value`
 * @param value a value to escape
 * @returns the escaped value, suitable for use in a `RegExp` pattern
 */
export function escapeRegExp(value: string): string {
    // from MDN: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
    // $& means the whole matched string
    return value.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Create a regex that does in-order searches for the specified strings
 *
 * @example `T i a t` should match `This is a test`.
 * @param queries text to search (in order)
 * @param flags RegExp flags (`g` for global, `i` for case-insensitive)
 * @returns a `RegExp` object
 */
export function searchInOrder(queries: string[], flags: string = 'gi'): RegExp {
    const parts = queries.map(part => escapeRegExp(part));
    const pattern = ['(?=.*)(', parts.join(')(?=.*'), ')'].join('');
    return new RegExp(pattern, flags);
}

/**
 * Create a regex that that matches any of the specified strings
 *
 * @param queries text to search (any order)
 * @param flags RegExp flags (`g` for global, `i` for case-insensitive)
 * @returns a `RegExp` object
 */
export function searchAny(queries: string[], flags: string = 'gi'): RegExp {
    const parts = queries.map(part => escapeRegExp(part));
    const pattern = ['(', parts.join('|'), ')'].join('');
    return new RegExp(pattern, flags);
}
